import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircle, 
  Clock,
  X,
  Info,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const CompletedProjects = () => {
  const { token, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [allProjectTasks, setAllProjectTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [complexityConfig, setComplexityConfig] = useState({});
  const [taskTimings, setTaskTimings] = useState({});
  const [teams, setTeams] = useState([]);
  const [productionLocations, setProductionLocations] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [clientFilterDialogOpen, setClientFilterDialogOpen] = useState(false);
  const [tempClientFilter, setTempClientFilter] = useState([]);
  
  // Search and Filter State
  const [projectSearch, setProjectSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [jobLocationFilter, setJobLocationFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'completed' | 'cancelled'
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalProjectsCount, setTotalProjectsCount] = useState(0);
  const [allJobLocations, setAllJobLocations] = useState([]);

  // Task Dialog State
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  // Activity Log Modal State
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [currentTaskTitle, setCurrentTaskTitle] = useState('');
  const [currentTaskActivities, setCurrentTaskActivities] = useState([]);

  useEffect(() => {
    if (token && user) {
      fetchProjects();
    }
    fetchComplexityConfig();
    if (token && (user?.role === 'production' || user?.role === 'production_lead')) {
      fetchTeams();
      fetchProductionLocations();
    }
  }, [token, user, currentPage, itemsPerPage, projectSearch, priorityFilter, jobLocationFilter, dateFrom, dateTo, clientFilter, statusFilter]);

  // Load persistent client filter preferences for PM/Admin
  useEffect(() => {
    const loadPreferences = async () => {
      if (!token || !user) return;
      if (user.role !== 'pm' && user.role !== 'admin') return;

      try {
        const response = await api.get('/user/preferences', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const storedFilter = Array.isArray(response.data?.client_filter)
          ? response.data.client_filter.filter((c) => typeof c === 'string' && c.trim().length > 0)
          : [];
        setClientFilter(storedFilter);
      } catch (err) {
        console.error('[CompletedProjects] Failed to load user preferences', err);
      }
    };

    loadPreferences();
  }, [token, user]);

  useEffect(() => {
    if (selectedProject && selectedProject.id) {
      // Prefer already-fetched archived tasks if present to avoid losing history
      if (selectedProject.tasks && selectedProject.tasks.length > 0) {
        setProjectTasks(selectedProject.tasks);
      } else {
        fetchProjectTasks(selectedProject.id);
      }
      fetchProjectAuditLogs(selectedProject.id);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (projectTasks.length > 0) {
      projectTasks.forEach(task => fetchTaskTimings(task.id));
    }
  }, [projectTasks]);

  // Recalculate timings from audit logs when they become available
  useEffect(() => {
    if (auditLogs.length > 0 && projectTasks.length > 0) {
      projectTasks.forEach(task => {
        // Try multiple ways to match audit logs to tasks
        const taskLogs = auditLogs.filter(log => {
          // Direct task_id match
          if (String(log.task_id) === String(task.id)) return true;
          
          // task_id in details
          if (log.details?.task_id && String(log.details.task_id) === String(task.id)) return true;
          if (log.details?.taskId && String(log.details.taskId) === String(task.id)) return true;
          
          // Match by task name in action
          if (log.action && task.name && log.action.toLowerCase().includes(task.name.toLowerCase())) return true;
          
          // Match by task name in description
          if (log.description && task.name && log.description.toLowerCase().includes(task.name.toLowerCase())) return true;
          
          return false;
        });
        
        if (taskLogs.length > 0 && (!taskTimings[task.id] || parseFloat(taskTimings[task.id].total || 0) === 0)) {
          const derivedTiming = calculateTimeBreakdown(taskLogs);
          if (derivedTiming && parseFloat(derivedTiming.total || 0) > 0) {
            setTaskTimings(prev => ({
              ...prev,
              [task.id]: derivedTiming
            }));
          }
        }
      });
    }
  }, [auditLogs, projectTasks]);

  const fetchComplexityConfig = async () => {
    try {
      const response = await api.get('/config/complexity');
      setComplexityConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch complexity config:', err);
      // Set default minimal config
      setComplexityConfig({});
    }
  };

  const fetchProjectTasks = async (projectId) => {
    try {
      const response = await api.get(`/pm/projects/${projectId}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectTasks(response.data);
    } catch (err) {
      console.error('Failed to fetch project tasks:', err);
      setError('Failed to fetch project tasks');
    }
  };

  const fetchProjectAuditLogs = async (projectId) => {
    try {
      const response = await api.get(`/pm/projects/${projectId}/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data);
    } catch (err) {
      console.error('Failed to fetch project audit logs:', err);
      // Don't error out the whole UI for this
    }
  };

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h}h ${m}m ${s}s`;
  };

  // Admin-only: force-close an orphaned open work session on a task and flip
  // the stuck user(s) back to idle. Used when a project completed while someone
  // still had a started/paused session that never closed.
  const handleForceEndSession = async (task) => {
    const def = new Date();
    def.setSeconds(0, 0);
    const local = new Date(def.getTime() - def.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    const input = window.prompt(
      `End the open session on "${task.name}" and set the affected user(s) to idle.\n\n` +
      `Enter the end date & time (YYYY-MM-DDTHH:MM), or leave blank for now:`,
      local
    );
    if (input === null) return; // cancelled
    let endIso = null;
    if (input.trim()) {
      const d = new Date(input.trim());
      if (isNaN(d.getTime())) { alert('Invalid date/time.'); return; }
      endIso = d.toISOString();
    }
    try {
      const res = await api.post(
        `/admin/tasks/${task.id}/force-end-session`,
        endIso ? { end_time: endIso } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.ended > 0) {
        alert(`Closed ${res.data.ended} open session(s). User(s) set to idle.`);
      } else {
        alert(res.data?.message || 'No open session found on this task.');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to end session.');
    }
  };

  const handleViewActivities = async (task) => {
    setCurrentTaskTitle(task.name || 'Task');
    try {
      const response = await api.get(`/pm/tasks/${task.id}/timings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const events = response.data;
      const processedEvents = [];
      for (let i = 0; i < events.length; i++) {
        const event = events[i];
        let duration = null;
        if (['pause', 'hold', 'query', 'end'].includes(event.event)) {
          const prevEvent = events[i - 1];
          if (prevEvent && ['start', 'resume'].includes(prevEvent.event)) {
            const start = new Date(prevEvent.ts);
            const end = new Date(event.ts);
            duration = (end - start) / 1000;
          }
        }
        processedEvents.push({ ...event, duration });
      }
      setCurrentTaskActivities(processedEvents.reverse());
      setActivityModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch activity log', err);
    }
  };

  const fetchTaskTimings = async (taskId) => {
    if (!taskId) return;
    try {
      const response = await api.get(`/pm/tasks/${taskId}/timings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const timings = calculateTimeBreakdown(response.data);
      setTaskTimings(prev => ({
        ...prev,
        [taskId]: timings
      }));
    } catch (err) {
      console.error(`[CompletedProjects] Failed to fetch timings for task ${taskId}`, err);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await api.get('/admin/teams', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('[CompletedProjects] Failed to fetch teams', err);
      setTeams([]);
    }
  };

  const fetchProductionLocations = async () => {
    try {
      const response = await api.get('/admin/production-locations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductionLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('[CompletedProjects] Failed to fetch production locations', err);
      setProductionLocations([]);
    }
  };

  const calculateTimeBreakdown = (events) => {
    if (!events || events.length === 0) {
      return { yesterday: '0.00', today: '0.00', total: '0.00', startTime: null, endTime: null };
    }

    let yesterday = 0;
    let today = 0;
    let total = 0;
    let currentStart = null;
    let firstStart = null;
    let lastEventTime = null;

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);

    // Sort events by timestamp just in case
    const sortedEvents = [...events].sort((a, b) => {
      const timeA = new Date(a.ts || a.timestamp || 0);
      const timeB = new Date(b.ts || b.timestamp || 0);
      return timeA - timeB;
    });

    for (const event of sortedEvents) {
      const eventTime = new Date(event.ts || event.timestamp);
      if (isNaN(eventTime.getTime())) continue;
      lastEventTime = eventTime;

      const eventType = (event.event || '').toLowerCase();
      if (eventType === 'start' || eventType === 'resume') {
        currentStart = eventTime;
        if (!firstStart) firstStart = eventTime;
      } else if ((eventType === 'pause' || eventType === 'hold' || eventType === 'end' || eventType === 'query' || eventType === 'task_rejected') && currentStart) {
        const duration = (eventTime - currentStart) / (1000 * 60 * 60);

        if (eventTime >= todayDate) {
          today += duration;
        } else if (eventTime >= yesterdayDate && eventTime < todayDate) {
          yesterday += duration;
        }

        total += duration;
        currentStart = null;
      }
    }

    // If currently running (started but not stopped), add time until now
    if (currentStart) {
      const now = new Date();
      const duration = (now - currentStart) / (1000 * 60 * 60);

      if (now >= todayDate) {
        today += duration;
      } else if (now >= yesterdayDate && now < todayDate) {
        yesterday += duration;
      }
      total += duration;
      lastEventTime = now;
    }

    return {
      yesterday: yesterday.toFixed(2),
      today: today.toFixed(2),
      total: total.toFixed(2),
      startTime: firstStart ? firstStart.toISOString() : null,
      endTime: lastEventTime ? lastEventTime.toISOString() : null
    };
  };

  const normalizeQrCode = (code) => {
    if (code === undefined || code === null) return '';
    return String(code).trim();
  };

  const getWorkflowSortKey = (task) => {
    if (!task) return 9999;
    const name = (task.name || '').toLowerCase();
    if (name === 'preflight') return 1;
    if (name === 'die creation') return 1.1;
    if (name === 'recreation') return 1.2;
    return typeof task.step_order === 'number' ? task.step_order : 9999;
  };

  const getStoredQrForTask = (task) => {
    if (!task) return null;
    const raw = task.qr_code || task.qr_value || task.qrCode || null;
    return normalizeQrCode(raw) || null;
  };

  const getTaskErrorSummary = (task) => {
    const reports = Array.isArray(task?.error_reports) ? task.error_reports : [];
    if (reports.length === 0) return null;
    const lastReason = reports[reports.length - 1]?.rejection_reason;
    return { count: reports.length, lastReason: lastReason ? String(lastReason) : '' };
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (projectSearch.trim()) params.search = projectSearch.trim();
      if (priorityFilter) params.priority = priorityFilter;
      if (jobLocationFilter) params.job_location = jobLocationFilter;
      if (clientFilter.length > 0) params.clients = clientFilter.join(',');
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (statusFilter) params.status_filter = statusFilter;

      const response = await api.get('/pm/projects/completed', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      const projectsData = response.data.data || response.data;
      const paginationInfo = response.data.pagination;

      const tasksMap = {};
      const locationsFromPage = new Set();
      const processedProjects = (projectsData || []).map(project => {
        const tasks = project.tasks || [];
        tasksMap[project.id] = tasks;
        const loc = (project.job_location || '').trim();
        if (loc) locationsFromPage.add(loc);
        return { ...project, allTasksCompleted: true };
      });

      setProjects(processedProjects);
      setAllProjectTasks(prev => ({ ...prev, ...tasksMap }));

      if (paginationInfo && paginationInfo.total !== undefined) {
        setTotalProjectsCount(paginationInfo.total);
      } else {
        setTotalProjectsCount(processedProjects.length);
      }

      if (locationsFromPage.size > 0) {
        setAllJobLocations(prev => {
          const combined = new Set(prev);
          locationsFromPage.forEach(loc => combined.add(loc));
          return Array.from(combined).sort();
        });
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const isProjectVisibleToUser = (project) => {
    // Admin can see all projects
    if (user?.role === 'admin') return true;
    
    const userRole = user?.role;
    const projectTasks = allProjectTasks[project.id];
    
    // If no tasks loaded yet, show it (optimistic)
    if (!projectTasks || projectTasks.length === 0) return true;
    
    // Always show to preflight team
    if (userRole === 'preflight' || userRole === 'preflight_lead') return true;

    // For production roles, show projects that have a production task
    // and whose job_location matches this user's production locations (if configured)
    if (userRole === 'production' || userRole === 'production_lead') {
      const hasProductionTask = projectTasks.some(
        (t) => t.team === 'production' || t.name === 'Plate and Despatch' || t.step_order === 8
      );

      const userId = user?.id;
      if (!userId || !Array.isArray(teams) || teams.length === 0) {
        return hasProductionTask;
      }

      const userTeams = teams.filter((team) => {
        if (team.type !== 'production') return false;
        const members = Array.isArray(team.members) ? team.members : [];
        const leadIds = Array.isArray(team.lead_ids) ? team.lead_ids : [];
        const legacyLeadId = team.lead_id;
        return (
          members.includes(userId) ||
          leadIds.includes(userId) ||
          legacyLeadId === userId
        );
      });

      const locationIdSet = new Set();
      userTeams.forEach((team) => {
        (Array.isArray(team.location_ids) ? team.location_ids : []).forEach((locId) => {
          if (locId) locationIdSet.add(String(locId));
        });
      });

      const idToName = new Map();
      (productionLocations || []).forEach((loc) => {
        const id = loc.id || loc._id;
        if (id && loc.name) {
          idToName.set(String(id), String(loc.name));
        }
      });

      const allowedLocationNames = Array.from(locationIdSet)
        .map((id) => idToName.get(id))
        .filter((name) => typeof name === 'string' && name.trim().length > 0)
        .map((name) => name.trim().toLowerCase());

      const projectLocation = (project.job_location || '').trim().toLowerCase();

      if (allowedLocationNames.length === 0 || !projectLocation) {
        return hasProductionTask;
      }

      const matchesLocation = allowedLocationNames.includes(projectLocation);
      return hasProductionTask && matchesLocation;
    }
    
    // For non-preflight teams, check if Preflight task is completed
    const preflightTask = projectTasks.find(t => t.step_number === 1 || t.name === 'Preflight');
    
    // If Preflight is not completed, hide project from other teams
    if (!preflightTask || preflightTask.status !== 'completed') {
      return false;
    }
    
    // Now check which team the user belongs to and if their task is available
    const teamTaskMap = {
      'cmd': 3,
      'cmd_lead': 3,
      'prepress': 4,
      'prepress_lead': 4,
      'qc': 7,
      'qc_lead': 7,
      'production': 8,
      'production_lead': 8,
      'pm': null
    };
    
    // PM can see all projects once preflight is completed
    if (userRole === 'pm') return true;
    
    // Get the step number for this user's team
    const userTaskStep = teamTaskMap[userRole];
    if (userTaskStep === null) return true;
    if (!userTaskStep) return false;
    
    // Find the user's team task
    const userTask = projectTasks.find(t => t.step_number === userTaskStep || t.step_order === userTaskStep);
    if (!userTask) return false;
    
    // Check if all tasks before user's task are completed
    const allPreviousCompleted = projectTasks.every(t => {
      if (t.step_number > userTaskStep || t.step_order > userTaskStep) return true;
      if (t.step_number === userTaskStep || t.step_order === userTaskStep) return true;
      return t.status === 'completed';
    });
    
    return allPreviousCompleted;
  };

  const handleClientFilterChange = async (newFilter) => {
    setClientFilter(newFilter);
    setCurrentPage(1);

    if (!token || !user) return;
    if (user.role !== 'pm' && user.role !== 'admin') return;

    try {
      await api.put('/user/preferences', {
        client_filter: newFilter
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('[CompletedProjects] Failed to save client filter preference', err);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'fast_track': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-orange-100 text-orange-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'on_hold': return 'bg-orange-100 text-orange-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'query': return 'bg-purple-100 text-purple-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'not_started': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDisplayTaskName = (task) => {
    if (!task) return '';
    const round = task.round || 1;
    return round > 1 ? `${task.name} (R${round})` : task.name;
  };

  const formatHours = (hours) => {
    if (hours === undefined || hours === null || isNaN(hours)) return '0h 0m 0s';
    
    // Safely round total seconds to handle floating point issues
    let totalSeconds = Math.round(hours * 3600);
    
    // Calculate components
    const h = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    
    return `${h}h ${m}m ${s}s`;
  };

  const availableClients = Array.from(
    new Set(
      projects
        .map((p) => (p.client || '').trim())
        .filter((name) => name.length > 0)
    )
  ).sort();

  if (loading && projects.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading completed projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Completed Projects</h1>
          {/* Status filter tabs */}
          <div className="flex gap-2">
            {[
              { value: '', label: 'All', icon: null },
              { value: 'completed', label: 'Completed', icon: <CheckCircle className="w-4 h-4" /> },
              { value: 'cancelled', label: 'Cancelled', icon: <XCircle className="w-4 h-4" /> }
            ].map(({ value, label, icon }) => (
              <button
                key={value}
                onClick={() => { setStatusFilter(value); setCurrentPage(1); }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-md font-medium border transition-colors ${
                  statusFilter === value
                    ? value === 'cancelled'
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button
              onClick={fetchProjects}
              className="ml-2 underline hover:no-underline font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Projects Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Filter Bar */}
          <div className={`p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2 ${
            (projectSearch || priorityFilter || jobLocationFilter || dateFrom || dateTo || clientFilter.length > 0 || statusFilter) ? 'bg-sky-200' : ''
          }`}>
            <h2 className="text-lg font-semibold">
              {statusFilter === 'cancelled' ? 'Cancelled Projects' : statusFilter === 'completed' ? 'Completed Projects' : 'All Archived Projects'}
            </h2>
            <div className="flex items-center flex-wrap gap-2">
              {(projectSearch || priorityFilter || jobLocationFilter || dateFrom || dateTo || clientFilter.length > 0 || statusFilter) && (
                <button
                  type="button"
                  onClick={() => {
                    setProjectSearch('');
                    setPriorityFilter('');
                    setJobLocationFilter('');
                    setDateFrom('');
                    setDateTo('');
                    setClientFilter([]);
                    setStatusFilter('');
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 text-sm text-white bg-red-500 hover:bg-red-700 rounded-md border border-gray-300 transition-colors"
                  title="Clear all filters"
                >
                  Clear
                </button>
              )}
              <input
                type="text"
                placeholder="Search projects..."
                value={projectSearch}
                onChange={(e) => { setProjectSearch(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={priorityFilter}
                onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Priorities</option>
                <option value="normal">Normal</option>
                <option value="fast_track">Fast Track</option>
              </select>
              <select
                value={jobLocationFilter}
                onChange={(e) => { setJobLocationFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Locations</option>
                {allJobLocations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
              <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {(user?.role === 'pm' || user?.role === 'admin') && (
                <button
                  type="button"
                  onClick={() => {
                    setTempClientFilter(clientFilter);
                    setClientFilterDialogOpen(true);
                  }}
                  className="px-3 py-2 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-300 transition-colors"
                >
                  Client Filter{clientFilter.length > 0 ? ` (${clientFilter.length})` : ''}
                </button>
              )}
            </div>
          </div>
          {(() => {
            // Server already filtered and paginated — display as-is
            const paginatedProjects = projects.filter(p => isProjectVisibleToUser(p));
            const totalPages = Math.ceil(totalProjectsCount / itemsPerPage) || 1;
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;

            if (paginatedProjects.length === 0 && !loading) {
              return (
                <div className="p-8 text-center text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg">
                    {statusFilter === 'cancelled' ? 'No cancelled projects found' : 'No completed projects found'}
                  </p>
                  <p className="text-sm mt-2">
                    {statusFilter === 'cancelled'
                      ? 'Cancelled projects will appear here'
                      : 'Projects will appear here once all tasks are completed'}
                  </p>
                </div>
              );
            }

            if (loading) {
              return (
                <div className="p-8 text-center text-gray-500">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                  <p className="text-sm">Loading...</p>
                </div>
              );
            }

            return (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                        <th className="px-6 py-3 font-medium">Job Code</th>
                        <th className="px-6 py-3 font-medium">Project</th>
                        <th className="px-6 py-3 font-medium">Status</th>
                        <th className="px-6 py-3 font-medium">Job Location</th>
                        <th className="px-6 py-3 font-medium">Priority</th>
                        <th className="px-6 py-3 font-medium">Task Complexity</th>
                        <th className="px-6 py-3 font-medium">Expected Hours</th>
                        <th className="px-6 py-3 font-medium">Actual Hours</th>
                        <th className="px-6 py-3 font-medium">Efficiency</th>
                        <th className="px-6 py-3 font-medium">Date</th>
                        <th className="px-6 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedProjects.map((project) => {
                        const projectTasks = allProjectTasks[project.id] || [];
                        const completedDate = projectTasks.length > 0 
                          ? new Date(Math.max(...projectTasks.map(t => new Date(t.updated_at)))).toLocaleDateString()
                          : 'N/A';

                        // Calculate metrics
                        const enabledProjectTasks = projectTasks.filter(task => 
                          !project.enabled_tasks || 
                          project.enabled_tasks.length === 0 || 
                          project.enabled_tasks.includes(task.step_order) ||
                          task.is_flexible ||
                          (task.round && task.round > 1)
                        );
                        const nonApprovalTasks = enabledProjectTasks.filter(task => task.task_type !== 'approval');
                        const totalActualHours = nonApprovalTasks.reduce((sum, task) => {
                          let hours = parseFloat(taskTimings[task.id]?.total || 0);
                          if (hours === 0) {
                            // Try all possible backend field names
                            hours = parseFloat(task.actual_hours || task.actual_time || task.total_hours || task.total_time || 0);
                          }
                          return sum + hours;
                        }, 0);

                        const projectComplexityKey = project?.complexity || 'simple';
                        const complexityTaskData = complexityConfig[projectComplexityKey];
                        
                        let totalExpectedMinutes = 0;
                        if (complexityTaskData && typeof complexityTaskData === 'object') {
                          nonApprovalTasks.forEach(task => {
                            const taskMinutes = complexityTaskData[task.name];
                            if (taskMinutes !== undefined) {
                              totalExpectedMinutes += taskMinutes;
                            }
                          });
                        }
                        
                        const totalExpectedHours = totalExpectedMinutes / 60;
                        const overallEfficiency = totalExpectedHours > 0 ? 
                          ((totalExpectedHours / totalActualHours) * 100) : 0;
                        
                        const efficiencyColor = overallEfficiency >= 100 ? 
                          (overallEfficiency >= 120 ? 'text-green-600' : 'text-blue-600') : 
                          (overallEfficiency >= 80 ? 'text-yellow-600' : 'text-red-600');

                        return (
                          <tr key={project.id} className={`hover:bg-gray-50 ${project.status === 'cancelled' ? 'bg-orange-50' : ''}`}>
                            
                            <td className="px-6 py-4">
                              <span className="font-mono text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded">
                                {project.job_code || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 max-w-xs">
                              <div>
                                <div className="font-medium text-gray-900 break-all whitespace-normal">{project.title}</div>
                                <div className="text-sm text-gray-500 break-all whitespace-normal">{project.client}</div>
                                {project.status === 'cancelled' && project.cancellation_comment && (
                                  <div className="text-xs text-orange-700 mt-1 italic" title={project.cancellation_comment}>
                                    ✕ {project.cancellation_comment.length > 60 ? `${project.cancellation_comment.slice(0, 60)}…` : project.cancellation_comment}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {project.status === 'cancelled' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  <XCircle className="w-3 h-3" /> Cancelled
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3" /> Completed
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {project.job_location || '-'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority)}`}>
                                {project.priority}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {project.complexity || 'N/A'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatHours(totalExpectedHours)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatHours(totalActualHours)}
                            </td>
                            <td className={`px-6 py-4 text-sm font-semibold ${efficiencyColor}`}>
                              {totalActualHours > 0 ? overallEfficiency.toFixed(1) : '0.0'}%
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {project.status === 'cancelled' ? (
                                <div className="flex items-center text-orange-700">
                                  <XCircle className="w-4 h-4 mr-2" />
                                  {project.cancelled_at
                                    ? new Date(project.cancelled_at).toLocaleDateString()
                                    : 'N/A'}
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                  {completedDate}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => {
                                  setSelectedProject(project);
                                  setTaskDialogOpen(true);
                                }}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full"
                                title="View Tasks"
                              >
                                <Info className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">Items per page:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-4">
                    {(() => {
                      const startItem = (currentPage - 1) * itemsPerPage + 1;
                      const endItem = Math.min(currentPage * itemsPerPage, totalProjectsCount);
                      return (
                        <>
                          <span className="text-sm text-gray-600">
                            Showing {totalProjectsCount > 0 ? startItem : 0} to {endItem} of {totalProjectsCount}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              ← Previous
                            </button>
                            <div className="flex items-center gap-1">
                              {(() => {
                                const pages = [];
                                const startPage = Math.max(1, currentPage - 3);
                                const endPage = Math.min(totalPages, currentPage + 3);
                                for (let page = startPage; page <= endPage; page += 1) {
                                  pages.push(page);
                                }
                                return pages.map((page) => (
                                  <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                      page === currentPage
                                        ? 'bg-blue-600 text-white'
                                        : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                                    }`}
                                  >
                                    {page}
                                  </button>
                                ));
                              })()}
                            </div>
                            <button
                              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                              disabled={currentPage === totalPages}
                              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Next →
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        {/* Client Filter Dialog */}
        {clientFilterDialogOpen && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Client Filter</h3>
                <button
                  onClick={() => setClientFilterDialogOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <p className="text-sm text-gray-600 mb-3">
                  Select one or more clients to filter completed projects. Leave all unchecked to see projects for all clients.
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setTempClientFilter(availableClients)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-700"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setTempClientFilter([])}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-700"
                  >
                    Clear All
                  </button>
                </div>
                {availableClients.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No clients found from completed projects.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {availableClients.map((name) => (
                      <label
                        key={name}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={tempClientFilter.includes(name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTempClientFilter((prev) =>
                                prev.includes(name) ? prev : [...prev, name]
                              );
                            } else {
                              setTempClientFilter((prev) =>
                                prev.filter((c) => c !== name)
                              );
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setClientFilterDialogOpen(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleClientFilterChange(tempClientFilter);
                    setClientFilterDialogOpen(false);
                  }}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Task Details Modal */}
        {taskDialogOpen && selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl max-h-screen overflow-hidden flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedProject?.title} - Task Details
                </h3>
                <button 
                  onClick={() => setTaskDialogOpen(false)} 
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 min-h-0">
                {/* Cancellation Banner */}
                {selectedProject?.status === 'cancelled' && (
                  <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-orange-800">Project Cancelled</p>
                      {selectedProject.cancellation_comment && (
                        <p className="text-sm text-orange-700 mt-0.5">Reason: {selectedProject.cancellation_comment}</p>
                      )}
                      {selectedProject.cancelled_by_name && (
                        <p className="text-xs text-orange-600 mt-0.5">
                          By {selectedProject.cancelled_by_name}
                          {selectedProject.cancelled_at ? ` on ${new Date(selectedProject.cancelled_at).toLocaleString()}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {/* Project Summary */}
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Project Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {(() => {
                      // Filter enabled tasks first
                      const enabledTasks = projectTasks.filter(task => 
                        !selectedProject.enabled_tasks || 
                        selectedProject.enabled_tasks.length === 0 || 
                        selectedProject.enabled_tasks.includes(task.step_order) ||
                        task.is_flexible ||
                        (task.round && task.round > 1)
                      );
                      const nonApprovalTasks = enabledTasks.filter(task => task.task_type !== 'approval');
                      
                      const totalActualHours = nonApprovalTasks.reduce((sum, task) => {
                        let hours = parseFloat(taskTimings[task.id]?.total || 0);
                        
                        // Check derived timing from audit logs
                        if (hours === 0 && auditLogs && auditLogs.length > 0) {
                          const taskLogs = auditLogs.filter(log => 
                            String(log.task_id) === String(task.id) || 
                            String(log.details?.task_id) === String(task.id)
                          );
                          if (taskLogs.length > 0) {
                             const derived = calculateTimeBreakdown(taskLogs);
                             hours = parseFloat(derived?.total || 0);
                          }
                        }

                        if (hours === 0) {
                          hours = parseFloat(task.actual_hours || task.actual_time || task.total_hours || task.total_time || 0);
                        }
                        return sum + (Number.isFinite(hours) ? hours : 0);
                      }, 0);

                      const projectComplexityKey = selectedProject?.complexity || 'simple';
                      const complexityTaskData = complexityConfig[projectComplexityKey];
                      
                      let totalExpectedMinutes = 0;
                      if (complexityTaskData && typeof complexityTaskData === 'object') {
                        nonApprovalTasks.forEach(task => {
                          const taskMinutes = complexityTaskData[task.name];
                          if (taskMinutes !== undefined) {
                            totalExpectedMinutes += taskMinutes;
                          }
                        });
                      }
                      
                      const totalExpectedHours = totalExpectedMinutes / 60;
                      const overallEfficiency = totalExpectedHours > 0 ? 
                        ((totalExpectedHours / totalActualHours) * 100) : 0;
                      
                      const efficiencyColor = overallEfficiency >= 100 ? 
                        (overallEfficiency >= 120 ? 'text-green-600' : 'text-blue-600') : 
                        (overallEfficiency >= 80 ? 'text-yellow-600' : 'text-red-600');

                      // Aggregate internal/external error counts across all tasks in the project
                      let projectInternalErrors = 0;
                      let projectExternalErrors = 0;

                      projectTasks.forEach((task) => {
                        if (Array.isArray(task.error_reports)) {
                          task.error_reports.forEach((report) => {
                            if (Array.isArray(report.internal_error_categories)) {
                              projectInternalErrors += report.internal_error_categories.length;
                            }
                            if (Array.isArray(report.external_error_categories)) {
                              projectExternalErrors += report.external_error_categories.length;
                            }
                          });
                        }
                      });

                      return (
                        <>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-800">
                              {formatHours(totalActualHours)}
                            </div>
                            <div className="text-sm text-gray-500">Actual Hours</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-800">
                              {formatHours(totalExpectedHours)}
                            </div>
                            <div className="text-sm text-gray-500">Expected Hours</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${efficiencyColor}`}>
                              {totalActualHours > 0 ? overallEfficiency.toFixed(1) : '0.0'}%
                            </div>
                            <div className="text-sm text-gray-500">Overall Efficiency</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-800">
                              {projectInternalErrors} / {projectExternalErrors}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Internals / Externals</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Tasks Table */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 font-medium text-sm">Step</th>
                        <th className="px-6 py-3 font-medium text-sm">Task</th>
                        <th className="px-6 py-3 font-medium text-sm">Type</th>
                        <th className="px-6 py-3 font-medium text-sm">Complexity</th>
                        <th className="px-6 py-3 font-medium text-sm">Status</th>
                        <th className="px-6 py-3 font-medium text-sm">Assigned To</th>
                        <th className="px-6 py-3 font-medium text-sm">Start Time</th>
                        <th className="px-6 py-3 font-medium text-sm">End Time</th>
                        <th className="px-6 py-3 font-medium text-sm">Working Time</th>
                        <th className="px-6 py-3 font-medium text-sm">Efficiency</th>
                        <th className="px-6 py-3 font-medium text-sm"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {projectTasks
                        // For completed projects, show all tasks across rounds
                        .sort((a, b) => getWorkflowSortKey(a) - getWorkflowSortKey(b))
                        .map((task) => {
                          const taskTiming = taskTimings[task.id] || { total: 0 };
                          
                          // Fallback timing from project-wide audit logs if per-task timings are missing
                          const taskLogs = (auditLogs || []).filter(log => {
                            if (String(log.task_id) === String(task.id)) return true;
                            if (log.details?.task_id && String(log.details.task_id) === String(task.id)) return true;
                            if (log.details?.taskId && String(log.details.taskId) === String(task.id)) return true;
                            if (log.action && task.name && log.action.toLowerCase().includes(task.name.toLowerCase())) return true;
                            if (log.description && task.name && log.description.toLowerCase().includes(task.name.toLowerCase())) return true;
                            return false;
                          });
                          
                          const derivedTiming = taskLogs.length > 0 ? calculateTimeBreakdown(taskLogs) : null;

                          const taskQr = getStoredQrForTask(task);
                          const taskComplexityKey = task.complexity || 'simple';
                          const complexityData = complexityConfig[taskComplexityKey];
                          let expectedMinutes = 0;
                          
                          if (complexityData && typeof complexityData === 'object') {
                            expectedMinutes = complexityData[task.name] || 0;
                          }
                          
                          const expectedHours = expectedMinutes / 60;
                          let actualHours = parseFloat(taskTiming.total || 0);
                          
                          // Fallback to derived timing if still 0
                          if (actualHours === 0 && derivedTiming && derivedTiming.total > 0) {
                            actualHours = derivedTiming.total;
                          }

                          // Fallback to task field if timing is still 0
                          if (actualHours === 0) {
                            actualHours = parseFloat(task.actual_hours || task.actual_time || task.total_hours || task.total_time || 0);
                          }

                          const taskEfficiency = expectedHours > 0 ? 
                            ((expectedHours / actualHours) * 100) : 0;
                          
                          const efficiencyColor = task.task_type === 'approval' ? 'text-gray-400' : 
                            (taskEfficiency >= 100 ? 
                              (taskEfficiency >= 120 ? 'text-green-600' : 'text-blue-600') : 
                              (taskEfficiency >= 80 ? 'text-yellow-600' : 'text-red-600'));

                          // Calculate start time with intelligent fallbacks
                          let startTimestamp = taskTiming.startTime || derivedTiming?.startTime || task.started_at || task.start_time;
                          
                          // If we still don't have a start time but have an end time, infer it
                          if (!startTimestamp) {
                            const endTime = task.completed_at || task.updated_at;
                            if (endTime && actualHours > 0) {
                              // Calculate start time by subtracting actual hours from end time
                              const endDate = new Date(endTime);
                              const startDate = new Date(endDate.getTime() - (actualHours * 3600 * 1000));
                              startTimestamp = startDate.toISOString();
                            } else if (endTime) {
                              // If no duration, use end time as start (instantaneous task)
                              startTimestamp = endTime;
                            }
                          }
                          
                          const endTimestamp = taskTiming.endTime || derivedTiming?.endTime || task.completed_at || task.updated_at;

                          return (
                            <tr key={task.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm font-medium text-gray-700">
                                {task.step_order}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                                <div>{getDisplayTaskName(task)}</div>
                                {taskQr && (
                                  <div className="text-xs text-gray-500 mt-1 font-normal">QR: {taskQr}</div>
                                )}
                                {getTaskErrorSummary(task) && (
                                  <div className="text-xs text-red-600 mt-1 font-normal">
                                    Errors: {getTaskErrorSummary(task).count}
                                    {getTaskErrorSummary(task).lastReason ? ` — ${getTaskErrorSummary(task).lastReason}` : ''}
                                  </div>
                                )}
                                {task.status === 'rejected' && task.rejected_reason && (
                                  <div className="text-xs text-red-600 mt-1 font-normal">Rejected: {task.rejected_reason}</div>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  task.task_type === 'approval' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {task.task_type}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                <div className="flex flex-col">
                                  <span>{task.complexity || 'N/A'}</span>
                                  {expectedHours > 0 && (
                                    <span className="text-xs text-gray-500">
                                      ({formatHours(expectedHours)})
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                  {task.status.replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                <div className="flex flex-col">
                                  <span>{task.assigned_user_name || task.assigned_to_name || (task.assigned_to ? 'Assigned' : 'Unassigned')}</span>
                                  {task.assigned_at && (
                                    <span className="text-xs text-gray-500 mt-0.5">
                                      Assigned: {new Date(task.assigned_at).toLocaleString()}
                                    </span>
                                  )}
                                  {task.status === 'completed' && task.completed_by_name && (
                                    <span className="text-xs text-green-600 mt-1">
                                      Completed by: {task.completed_by_name}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                {startTimestamp ? new Date(startTimestamp).toLocaleString() : '-'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                {endTimestamp ? new Date(endTimestamp).toLocaleString() : '-'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                {formatHours(actualHours)}
                              </td>
                              <td className={`px-6 py-4 text-sm font-semibold ${efficiencyColor}`}>
                                {task.task_type === 'approval' ? '-' : 
                                  (actualHours > 0 ? taskEfficiency.toFixed(1) : '0.0')}%
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {user?.role === 'admin' &&
                                    ['in_progress', 'paused', 'on_hold', 'query'].includes(task.status) &&
                                    task.task_type !== 'approval' && (
                                    <button
                                      onClick={() => handleForceEndSession(task)}
                                      className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                                      title="Force-end this open session and set the user to idle"
                                    >
                                      End session
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleViewActivities(task)}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full"
                                    title="View Activity Log"
                                  >
                                    <Info className="w-5 h-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Audit Logs Section */}
                {auditLogs.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">Audit Logs</h4>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase">Time</th>
                            <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase">User</th>
                            <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase">Action</th>
                            <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {auditLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {new Date(log.timestamp).toLocaleString()}
                              </td>
                              <td className="px-6 py-3 text-sm font-medium text-gray-900">
                                {log.user_name}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-700">
                                {log.action}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-500">
                                <div className="max-w-xs truncate" title={JSON.stringify(log.details, null, 2)}>
                                  {JSON.stringify(log.details)}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setTaskDialogOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Activity Log Modal */}
        {activityModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">
                  Activity Log: {currentTaskTitle}
                </h3>
                <button onClick={() => setActivityModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider sticky top-0">
                      <th className="px-6 py-3 font-medium">Time</th>
                      <th className="px-6 py-3 font-medium">Event</th>
                      <th className="px-6 py-3 font-medium">User</th>
                      <th className="px-6 py-3 font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentTaskActivities.map((activity, index) => (
                      <tr key={activity.id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                          {new Date(activity.ts).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            activity.event.includes('start') || activity.event.includes('resume') ? 'bg-green-100 text-green-800' :
                            activity.event.includes('end') || activity.event.includes('pause') ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {activity.event.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {activity.user_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                          {formatDuration(activity.duration)}
                        </td>
                      </tr>
                    ))}
                    {currentTaskActivities.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                          No activities recorded for this task.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setActivityModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletedProjects;
